import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";

import Modal from "../../crmpostventa-shared/components/Modal";
import { formatTime } from "../../crmpostventa-shared/crmFormat";
import { altasBajasHref, isPlainClick } from "../../crmpostventa-shared/crmLinks";
import { createCorrelationId } from "../../crmpostventa-rollo/nuevaGestion/nuevaGestionModel";
import { fichaGestionNoticeTitle } from "../../crmpostventa-ipc/fichaGestion";
import { crmPostventaFichaApi } from "../crmPostventaFichaApi";
import EditorStep from "./EditorStep";
import RevisionStep from "./RevisionStep";
import {
  CANAL_POR_DEFECTO,
  DESCARTAR_TEXTO,
  MENSAJES,
  calcularEstado,
  camposDe,
  erroresDeRespuesta,
  gestionRevisionText,
  isConflicto,
  motivoGestionAuto,
  originalesDe,
  plural,
  validarCampo,
  valoresAEnviar,
} from "./fichaEditorModel";

const CONFLICTO_TEXTO = "No se guardó nada. Mirá los cambios nuevos antes de guardar los tuyos.";
const RECARGA_ERROR = "No pudimos traer los cambios nuevos. Probá de nuevo.";
const ERROR_TEXTO = "No se guardó nada: ni los datos ni el historial. Lo que escribiste se mantiene.";
const ERROR_CON_GESTION_TEXTO = "No se guardó nada: ni los datos, ni el historial, ni la gestión. Lo que escribiste se mantiene.";

const without = (map, key) => {
  if (!(key in map)) return map;
  const next = { ...map };
  delete next[key];
  return next;
};

const joinY = (items) => (items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} y ${items[items.length - 1]}`);

// Fields another asesor changed between two loads of the ficha, with who and when (the newest history entry).
const remoteDe = (anterior, nueva) => {
  const antes = originalesDe(anterior);
  const ahora = originalesDe(nueva);
  const campos = camposDe(nueva).filter((campo) => (antes[campo.clave] ?? "") !== (ahora[campo.clave] ?? ""));
  if (!campos.length) return null;
  const ultimo = nueva?.ultimosCambios?.[0];
  const nombre = ultimo?.usuario?.nombre || "";
  const hora = ultimo?.fecha ? formatTime(ultimo.fecha) : "";
  const aLas = hora ? ` a las ${hora}` : "";
  return {
    keys: campos.map((campo) => campo.clave),
    nota: `Actualizado por ${nombre || "otro asesor"}${aLas}`,
    titulo: `${nombre || "Otro asesor"} cambió ${plural(campos.length, "campo", "campos")} de esta terminal${aLas} mientras editabas: ${joinY(campos.map((campo) => campo.etiqueta))}.`,
  };
};

/**
 * Editor of the Ficha in a large Modal (full screen on mobile): Paso 1 de 2 edits the fields, Paso 2 de 2 reviews the
 * changes and saves them through POST api/crm-postventa-ficha/guardar (T7 of odd/tasks/ficha-cliente.md, S11; design
 * Prompts 2 and 3). The save sends only the fields changed by hand, the ficha's versionToken and one correlationId per
 * body, reused by "Reintentar". onSaved(response) runs after a save; onFichaRecargada(ficha) after "Ver cambios nuevos".
 *
 * Phase 2 (T11, S12; FD2–FD6, FD8, FD11): from a gestión (gestionContext) the save sends its gestionId and the server
 * finalizes it as Resuelta; a gestión error keeps everything typed and offers "Volver a la gestión". The informal save
 * can tick "Registrar en Gestiones IPC como gestión finalizada", which sends registrarGestion { motivoCodigo, canal }:
 * the motive follows the hand-edited sections until the asesor picks one, the canal starts in Otro.
 */
export default function FichaEditorModal({
  ficha,
  catalogos,
  gestionContext,
  darkMode = false,
  returnFocusRef,
  onClose,
  onSaved,
  onFichaRecargada,
  onNavigate,
}) {
  const [base, setBase] = useState(ficha);
  const [edits, setEdits] = useState({});
  const [tocados, setTocados] = useState(() => new Set());
  const [serverErrores, setServerErrores] = useState({});
  const [step, setStep] = useState("edit");
  const [motivo, setMotivo] = useState("");
  const [motivoError, setMotivoError] = useState(null);
  const [observacion, setObservacion] = useState("");
  const [observacionError, setObservacionError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [conflicto, setConflicto] = useState(null);
  const [recargando, setRecargando] = useState(false);
  const [remote, setRemote] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [confirmar, setConfirmar] = useState(null);
  const [registrar, setRegistrar] = useState(false);
  // null while the gestión motive follows the edited sections; the code the asesor picked by hand otherwise.
  const [motivoGestionElegido, setMotivoGestionElegido] = useState(null);
  const [canal, setCanal] = useState(CANAL_POR_DEFECTO);
  const attemptRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const secciones = useMemo(() => base?.secciones || [], [base]);
  const campos = useMemo(() => camposDe(base), [base]);
  const porClave = useMemo(() => new Map(campos.map((campo) => [campo.clave, campo])), [campos]);
  const originales = useMemo(() => originalesDe(base), [base]);
  const estado = useMemo(() => calcularEstado(campos, originales, edits, catalogos), [campos, originales, edits, catalogos]);

  const errorCliente = (clave) => (estado.manuales.has(clave) ? validarCampo(porClave.get(clave), estado.valores[clave], estado.valores) : null);
  const errorDe = (clave) => serverErrores[clave] || (tocados.has(clave) ? errorCliente(clave) : null);
  const errores = campos.filter((campo) => errorDe(campo.clave));
  const hayCambios = estado.cambios.length > 0;
  const cabecera = base?.cabecera || {};
  const clienteId = base?.clienteId;
  const altasHref = altasBajasHref({ clienteId });
  const desdeGestion = Boolean(gestionContext?.gestionId);
  const registra = registrar && !desdeGestion;
  const motivoGestion = motivoGestionElegido || motivoGestionAuto(estado.cambios);
  const conGestion = desdeGestion || registra;

  const pedirDescarte = (accion) => {
    if (hayCambios) setConfirmar(() => accion);
    else accion();
  };

  const cerrar = () => {
    if (saving) return;
    pedirDescarte(() => onClose?.());
  };

  const onLink = (event, href) => {
    if (!onNavigate || !isPlainClick(event)) return;
    event.preventDefault();
    if (saving) return;
    pedirDescarte(() => {
      onClose?.();
      onNavigate(href);
    });
  };

  const handleChange = (clave, value) => {
    setEdits((prev) => ({ ...prev, [clave]: value }));
    setServerErrores((prev) => without(prev, clave));
  };

  const handleBlur = (clave) => {
    setTocados((prev) => (prev.has(clave) ? prev : new Set(prev).add(clave)));
  };

  const handleUndo = (clave) => {
    setEdits((prev) => without(prev, clave));
    setServerErrores((prev) => without(prev, clave));
    setTocados((prev) => {
      const next = new Set(prev);
      next.delete(clave);
      return next;
    });
  };

  const revisar = () => {
    const pendientes = [...estado.manuales].filter((clave) => serverErrores[clave] || errorCliente(clave));
    setTocados((prev) => new Set([...prev, ...estado.manuales]));
    if (pendientes.length || !hayCambios) return;
    setAviso(null);
    setStep("review");
  };

  const guardar = async () => {
    if (saving) return;
    const motivoLimpio = motivo.trim();
    if (!motivoLimpio) {
      setMotivoError(MENSAJES.motivo);
      document.getElementById("crm-ficha-fe-motivo")?.focus();
      return;
    }

    const body = {
      clienteId,
      valores: valoresAEnviar(estado),
      versionToken: base?.versionToken,
      motivo: motivoLimpio,
      observacion: observacion.trim() || null,
    };
    if (desdeGestion) body.gestionId = gestionContext.gestionId;
    else if (registra) body.registrarGestion = { motivoCodigo: motivoGestion, canal };
    // The same body keeps its correlationId, so "Reintentar" cannot save twice; any change gets a new one.
    const key = JSON.stringify(body);
    if (attemptRef.current?.key !== key) attemptRef.current = { key, correlationId: createCorrelationId() };

    setSaving(true);
    setSaveError(null);
    setConflicto(null);
    setMotivoError(null);
    setObservacionError(null);
    try {
      const response = await crmPostventaFichaApi.guardar({ ...body, correlationId: attemptRef.current.correlationId });
      attemptRef.current = null;
      if (mountedRef.current) setSaving(false);
      onSaved?.(response);
      return;
    } catch (error) {
      if (!mountedRef.current) return;
      const rechazo = erroresDeRespuesta(error);
      if (isConflicto(error)) {
        setConflicto(CONFLICTO_TEXTO);
      } else if (rechazo) {
        setMotivoError(rechazo.motivo);
        setObservacionError(rechazo.observacion);
        if (Object.keys(rechazo.campos).length) {
          setServerErrores(rechazo.campos);
          setAviso(error.message || "Revisá los datos marcados.");
          setStep("edit");
        }
      } else {
        const status = Number(error?.status) || 0;
        setSaveError(status >= 400 && status < 500 && error?.message ? error.message : conGestion ? ERROR_CON_GESTION_TEXTO : ERROR_TEXTO);
      }
    }
    setSaving(false);
  };

  const verCambiosNuevos = async () => {
    setRecargando(true);
    try {
      const nueva = await crmPostventaFichaApi.obtenerFicha(clienteId);
      if (!mountedRef.current) return;
      setRemote(remoteDe(base, nueva));
      setBase(nueva);
      setServerErrores({});
      setConflicto(null);
      setStep("edit");
      onFichaRecargada?.(nueva);
    } catch (error) {
      if (mountedRef.current) setConflicto(RECARGA_ERROR);
    } finally {
      if (mountedRef.current) setRecargando(false);
    }
  };

  const enRevision = step === "review";
  const titleId = "crm-ficha-fe-title";
  const terminal = cabecera.nDeTerminal ? `Terminal ${cabecera.nDeTerminal}` : "";
  const stepLabel = [cabecera.nombreDeFantasia, terminal, enRevision ? "Paso 2 de 2" : "Paso 1 de 2"].filter(Boolean).join(" · ");
  const status = hayCambios ? (
    <>
      <span className="crm-ficha__fe-dot" aria-hidden="true" />
      {plural(enRevision ? estado.cambios.length : estado.modificados.length, "campo modificado", "campos modificados")}
    </>
  ) : (
    "Sin cambios todavía"
  );

  const footer = enRevision ? (
    <>
      <span className="crm-rollo__grow crm-ficha__fe-status">{status}</span>
      <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary" disabled={saving} onClick={() => setStep("edit")}>
        <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
        Volver al editor
      </button>
      <button type="button" className="crm-rollo__btn crm-rollo__btn--primary" disabled={saving || recargando} aria-busy={saving || undefined} onClick={guardar}>
        {saving ? (
          <>
            <span className="crm-rollo__spinner" aria-hidden="true" />
            Guardando…
          </>
        ) : registra ? (
          "Guardar y registrar gestión"
        ) : (
          "Guardar cambios"
        )}
      </button>
    </>
  ) : (
    <>
      <span className="crm-rollo__grow crm-ficha__fe-status" aria-live="polite">
        {status}
        {errores.length ? <span className="crm-ficha__fe-errn"> · {plural(errores.length, "error", "errores")} para corregir</span> : null}
      </span>
      <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary" onClick={cerrar}>
        Cancelar
      </button>
      <button type="button" className="crm-rollo__btn crm-rollo__btn--primary" disabled={!hayCambios || errores.length > 0} onClick={revisar}>
        Revisar cambios
      </button>
    </>
  );

  return (
    <>
      <Modal
        titleId={titleId}
        title={enRevision ? "Revisar cambios" : "Editar datos"}
        stepLabel={stepLabel}
        steps={2}
        step={enRevision ? 2 : 1}
        size="lg"
        darkMode={darkMode}
        closeDisabled={saving || Boolean(confirmar)}
        returnFocusRef={returnFocusRef}
        onClose={cerrar}
        headExtra={
          <span className="crm-rollo__s">
            {enRevision
              ? conGestion
                ? "Los datos, su historial y la gestión se guardan juntos."
                : "Los datos y su historial se guardan juntos."
              : "Los cambios se marcan con un punto. Podés vaciar un campo si el dato ya no corresponde."}
          </span>
        }
        bodyClassName="crm-ficha__fe-body"
        className="crm-ficha-editor"
        footer={footer}
      >
        {enRevision ? (
          <RevisionStep
            secciones={secciones}
            cambios={estado.cambios}
            gestionText={desdeGestion ? gestionRevisionText(gestionContext, cabecera) : null}
            motivo={motivo}
            motivoError={motivoError}
            observacion={observacion}
            observacionError={observacionError}
            saving={saving}
            saveError={saveError}
            conflicto={conflicto}
            recargando={recargando}
            onMotivo={(value) => {
              setMotivo(value);
              if (value.trim()) setMotivoError(null);
            }}
            onObservacion={(value) => {
              setObservacion(value);
              setObservacionError(null);
            }}
            registro={
              desdeGestion
                ? null
                : {
                    marcado: registrar,
                    motivo: motivoGestion,
                    elegidoAMano: Boolean(motivoGestionElegido),
                    canal,
                    onMarcar: setRegistrar,
                    onMotivo: setMotivoGestionElegido,
                    onCanal: setCanal,
                  }
            }
            gestionHref={desdeGestion ? gestionContext?.returnHref : null}
            onLink={onLink}
            onReintentar={guardar}
            onVerCambiosNuevos={verCambiosNuevos}
            onCancelarConflicto={cerrar}
          />
        ) : (
          <EditorStep
            secciones={secciones}
            estado={estado}
            originales={originales}
            errorDe={errorDe}
            remote={remote}
            aviso={aviso}
            gestionTitle={desdeGestion ? fichaGestionNoticeTitle(gestionContext, cabecera) : null}
            gestionHref={gestionContext?.returnHref}
            altasHref={altasHref}
            disabled={saving}
            catalogos={catalogos}
            onChange={handleChange}
            onBlur={handleBlur}
            onUndo={handleUndo}
            onLink={onLink}
          />
        )}
      </Modal>
      {confirmar ? (
        <Modal
          titleId="crm-ficha-fe-confirm"
          title="Descartar cambios"
          size="xs"
          role="alertdialog"
          darkMode={darkMode}
          className="crm-ficha-editor"
          onClose={() => setConfirmar(null)}
          footer={
            <>
              <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary" onClick={() => setConfirmar(null)}>
                Seguir editando
              </button>
              <button
                type="button"
                className="crm-rollo__btn crm-rollo__btn--danger"
                onClick={() => {
                  const accion = confirmar;
                  setConfirmar(null);
                  accion();
                }}
              >
                Descartar cambios
              </button>
            </>
          }
        >
          <p className="crm-rollo__confirm-text">{DESCARTAR_TEXTO}</p>
          <p className="crm-rollo__s">{plural(estado.modificados.length, "campo modificado se pierde", "campos modificados se pierden")}.</p>
        </Modal>
      ) : null}
    </>
  );
}
